import React, { useState, useCallback, useEffect } from 'react';
import { SmlsButton, SmlsModal } from '@smiles/smiles-ui-kit-react';
import { useHistory, useLocation } from 'react-router-dom';
import { ModalBody } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { getMemberInfo, generateTokenEmail } from 'services/core/services';
import { Config } from 'services/core/config';
import DefaultMsgPage from 'components/DefaultMsgPage';
import { loginDataLayer } from 'utils/dataLayer';

type HistoryState = {
  maskedPhone?: string;
  phoneNumber?: string;
  maskedEmail?: string;
  pushEmail?: boolean;
};

const HomePlaceholder: React.FC = () => {
  const TOKEN_NAME = process.env.REACT_APP_TOKEN_NAME || 'session-token';
  const [updateIsOpen, setUpdateIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const { t } = useTranslation('news');
  const history = useHistory();
  const location = useLocation();

  const getMember = async () => {
    const authToken = localStorage.getItem(TOKEN_NAME);

    if (authToken) {
      Config.saveTokenAuthUser({ accessToken: authToken });
    }
    try {
      const ret = await getMemberInfo();
      const { success } = ret;

      if (!success) {
        handleToggleError();
        return null;
      }

      const { emailMask, phoneNumberMask, phoneNumber, phoneDDINumber, phoneDDDNumber } = ret.data;

      setEmail(emailMask);
      return { emailMask, phoneNumberMask, phoneNumber: phoneDDINumber + phoneDDDNumber + phoneNumber };
    } catch (e) {
      return null;
    }
  };

  const handleGoHome = async () => {
    window.location.replace(`/home?upd=${new Date().getTime()}`);
  };

  const handleToggleUpdate = () => {
    setUpdateIsOpen((prev) => !prev);
  };

  const handleGoMyAccount = async () => {
    window.location.replace('/restrito/minha-conta/alterar-dados');
  };

  const handleToggleError = async () => {
    setHasError((prev) => !prev);
  };

  const handleUpdate = useCallback(() => {
    const phoneVerified = sessionStorage.getItem('phoneVerified');

    setIsLoading(true);
    if (!phoneVerified || phoneVerified === 'false') {
      const getPhone = async () => {
        const resp = await getMember();
        if (resp) {
          history.push('/validar/celular', {
            maskedPhone: resp.phoneNumberMask,
            phoneNumber: resp.phoneNumber,
            maskedEmail: resp.emailMask,
          } as HistoryState);
        }
      };
      getPhone();
      setIsLoading(false);
      return;
    }

    const emailVerified = sessionStorage.getItem('emailVerified');
    if (!emailVerified || emailVerified === 'false') {
      const sendEmail = async () => {
        try {
          await getMember();
          const resp = await generateTokenEmail();
          return resp;
        } catch (e) {
          return { success: false };
        }
      };

      sendEmail();
      handleToggleUpdate();
    }
    setIsLoading(false);
  }, [sessionStorage]);

  useEffect(() => {
    const { state } = location;
    const historyState = state as HistoryState;
    if (historyState && historyState.pushEmail) {
      const { maskedEmail } = state as HistoryState;
      setEmail(maskedEmail || '');
      setTimeout(async () => {
        try {
          const resp = await generateTokenEmail();
          setUpdateIsOpen(true);
          return resp;
        } catch (e) {
          return { success: false };
        }
      }, 1);
    }
  }, [location]);

  useEffect(() => {
    loginDataLayer.triggerPageViewEvent('atualizar-dados', 3);
  }, []);

  useEffect(() => {
    if (updateIsOpen === true) {
      loginDataLayer.triggerPageViewEvent('atualizar-dados/confirmar-email', 3);
    }
  }, [updateIsOpen]);

  return (
    <>
      <DefaultMsgPage
        traductionKey="account_update"
        onPrimary={handleUpdate}
        onSecondary={handleGoHome}
        image="profile"
        isPrimaryLoading={isLoading}
      />
      <SmlsModal className="smls-modal-validate__email" toggle={handleToggleUpdate} isOpen={updateIsOpen} type="right">
        <ModalBody>
          <div className="smls-modal-header">
            <h1>{t('confirm_your_account')}</h1>
          </div>
          <div className="smls-modal-text">
            <p>{t('confirm_your_account_message')}</p>
            <p>{email}</p>
          </div>
          <SmlsButton color="primary" text={t('understood')} onClick={handleGoHome} />
          <span>{t('not_your_email')}</span>
          <SmlsButton color="hyperlink" text={t('update_email')} onClick={handleGoMyAccount} />
        </ModalBody>
      </SmlsModal>

      <SmlsModal className="smls-modal-validate__email" toggle={handleToggleError} isOpen={hasError} type="right">
        <ModalBody>
          <div className="smls-modal-header">
            <h1>{t('generic_error_title')}</h1>
          </div>
          <div className="smls-modal-text">
            <p>{t('generic_error_content')}</p>
          </div>
          <SmlsButton color="primary" text={t('generic_error_primary')} onClick={handleToggleError} />
        </ModalBody>
      </SmlsModal>
    </>
  );
};

export default HomePlaceholder;
